import { useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { DefaultSetupSelectionUpdate } from 'app/frontend/base/api/default_setups'

type OptimisticPatch = {
  id: number
  settled: boolean
  apply: (setups: wowaudit.DefaultSetup[]) => wowaudit.DefaultSetup[]
}

type OptimisticContext = {
  previous?: wowaudit.DefaultSetup[]
  optimisticPatchId: number
}

export const applyDefaultSetupSelection = (
  setups: wowaudit.DefaultSetup[],
  setupId: number,
  selection: DefaultSetupSelectionUpdate,
): wowaudit.DefaultSetup[] =>
  setups.map((entry) => {
    if (entry.id !== setupId) return entry
    const key = selection.character_id.toString()
    const existingSelections = entry.selections ?? {}
    const existing = existingSelections[key] ?? {}
    return {
      ...entry,
      selections: {
        ...existingSelections,
        [key]: {
          ...existing,
          ...(selection.selected !== undefined
            ? { selected: selection.selected }
            : {}),
          ...(selection.role !== undefined ? { role: selection.role } : {}),
          ...(selection.spec_id !== undefined
            ? { spec_id: selection.spec_id }
            : {}),
          ...(selection.alt_character_id !== undefined
            ? { character_id: selection.alt_character_id }
            : {}),
        },
      },
    }
  })

const useDefaultSetupOptimisticUpdate = (queryKey: readonly unknown[]) => {
  const queryClient = useQueryClient()
  const pendingPatchesRef = useRef<OptimisticPatch[]>([])
  const nextPatchIdRef = useRef(1)

  const replayOptimisticPatches = (setups: wowaudit.DefaultSetup[]) =>
    pendingPatchesRef.current.reduce(
      (nextSetups, patch) => patch.apply(nextSetups),
      setups,
    )

  const clearSettledOptimisticPatches = () => {
    if (pendingPatchesRef.current.every((patch) => patch.settled)) {
      pendingPatchesRef.current = []
    }
  }

  const snapshot = async () => {
    await queryClient.cancelQueries({ queryKey })
    return queryClient.getQueryData<wowaudit.DefaultSetup[]>(queryKey)
  }

  const registerOptimisticPatch = (
    previous: wowaudit.DefaultSetup[] | undefined,
    apply: (setups: wowaudit.DefaultSetup[]) => wowaudit.DefaultSetup[],
  ): OptimisticContext => {
    const optimisticPatchId = nextPatchIdRef.current
    nextPatchIdRef.current += 1
    pendingPatchesRef.current = [
      ...pendingPatchesRef.current,
      { id: optimisticPatchId, settled: false, apply },
    ]
    queryClient.setQueryData<wowaudit.DefaultSetup[]>(
      queryKey,
      (current = []) => apply(current),
    )
    return { previous, optimisticPatchId }
  }

  const applyServerUpdates = (
    data: wowaudit.DefaultSetup | wowaudit.DefaultSetup[],
    context?: OptimisticContext,
  ) => {
    const setups = Array.isArray(data) ? data : [data]
    if (context) {
      pendingPatchesRef.current = pendingPatchesRef.current.map((patch) =>
        patch.id === context.optimisticPatchId
          ? { ...patch, settled: true }
          : patch,
      )
    }
    const byIdentity = new Map(
      setups.map((setup) => [
        `${setup.encounterId}:${setup.difficulty}`,
        setup,
      ]),
    )
    queryClient.setQueryData<wowaudit.DefaultSetup[]>(
      queryKey,
      (current = []) => {
        const remaining = new Map(byIdentity)
        const merged = current.map((entry) => {
          const key = `${entry.encounterId}:${entry.difficulty}`
          const server = remaining.get(key)
          if (!server) return entry
          remaining.delete(key)
          return server
        })
        return replayOptimisticPatches([...merged, ...remaining.values()])
      },
    )
    clearSettledOptimisticPatches()
  }

  const rollback = (context?: OptimisticContext) => {
    if (!context) return
    pendingPatchesRef.current = pendingPatchesRef.current.filter(
      (patch) => patch.id !== context.optimisticPatchId,
    )
    if (context.previous) {
      queryClient.setQueryData(
        queryKey,
        replayOptimisticPatches(context.previous),
      )
    }
    clearSettledOptimisticPatches()
  }

  return {
    snapshot,
    registerOptimisticPatch,
    applyServerUpdate: applyServerUpdates,
    applyServerUpdates,
    rollback,
  }
}

export default useDefaultSetupOptimisticUpdate
